<template>
    <page>
        <div class="shadow-25">
            <v-header :title="$t('upload and import file')" icon="cloud upload"></v-header>
            <card width="800px" style="margin: 0 auto;">

                <alert :trigger="success"
                       color="positive"
                       icon="done"
                       :html="responseMessage"></alert>

                <alert :trigger="success === false" v-for="( error,index ) in uploadErrors" :key="`error-${index}`"
                       color="negative"
                       icon="error">{{error}}</alert>

                <form @submit.prevent="upload"
                      class="q-pt-xs gutter-xs row">

                    <v-radio path="delimiter"
                             v-model="form.delimiter"
                             :label="$T('delimiter')"
                             :helper="$T('the character used to separate columns.')"
                             :options="delimiterOptions"
                             :form="form"
                             class="col-12">
                    </v-radio>

                    <v-select v-for="(column, i) in getColumns"
                              :key="i"
                              :path="`column${i}`"
                              v-model="form[`column${i}`]"
                              :label="$_t('the column :i is the field', {i: i+1})"
                              :options="getColumns"
                              :clearable="false"
                              :form="form"
                              class="col-12">
                    </v-select>

                    <!-- File -->
                    <v-file ref="file"
                            path="file"
                            :label="$t('select file')"
                            :url="url"
                            extensions=".csv"
                            color="highlight"
                            :helper="$T('save a csv file and upload it here.')"
                            @uploaded="receiveData"
                            :additional-fields="serializedForm"
                            :title="$T('select file')"
                            class="col-12">
                    </v-file>

                    <v-checkbox path="skip_first"
                                v-model="form.skip_first"
                                :label="$t('skip first line')"
                                :form="form"
                                class="col-12">
                    </v-checkbox>

                    <!-- Submit Button -->
                    <div class="row justify-center col-12 q-mt-md group">
                        <v-button outline
                                  :label="$t('Ver Información')"
                                  icon="info"
                                  color="info"
                                  class="no-shadow uppercase q-btn-iqos"
                                  @click.prevent="infoModalOpen('LendingPoint')"
                                  type="button">
                        </v-button>

                        <v-button outline
                                  :label="$t('download example')"
                                  icon="grid_on"
                                  color="info"
                                  class="no-shadow uppercase q-btn-iqos"
                                  @click.prevent="downloadSample(hasReverseFlowEnable ?
                                    'lending_points_rf_to_import.csv' : 'lending_points_to_import.csv')"
                                  type="button">
                        </v-button>

                        <v-button :label="$t('send')"
                                  color="primary"
                                  class="no-shadow uppercase q-btn-iqos"
                                  :loading="uploading"
                                  type="submit">
                            <template slot="loading">
                                <q-spinner-puff class="on-left" color="white"></q-spinner-puff>
                                {{ $t('sending...') | capitalize }}
                            </template>
                        </v-button>

                        <p v-if="uploading" class="col-12 q-mt-sm text-center text-negative">
                            {{ $T('the file will be processed after the upload.') }}
                            {{ $T('this could take a while until the imported data shows up in the list.') }}
                        </p>
                    </div>

                </form>

            </card>
        </div>

        <q-modal v-model="dragModal"
                 @drop.native.prevent.stop="onDrop"
                 @dragleave.native.prevent.stop="onDragLeave"
                 content-classes="row items-center justify-center upload-drop-zone">
            <h2 class="q-display-1 text-center">
                <q-icon name="cloud upload" color="faded" size="3em"></q-icon>
                <br>
                {{ $t('drop your file here') }}
            </h2>
        </q-modal>

        <q-modal v-model="infoModal" no-backdrop-dismiss no-esc-dismiss>
        <div>
            <v-header title="Guía de Importación" icon="info">
                <template slot="right">
                    <v-button flat rounded dense color="primary"
                              icon="close"
                              :label="$T('close')"
                              @click="infoModalClose"/>
                </template>
            </v-header>
            <card width="100%">
                <p>En esta tabla de relaciones podrás encontrar información necesaria para colocar en los excel de importación.</p>
                <div v-if="infoContent">
                    <div v-for="(items, category) in infoContent" :key="category">
                    <h3>{{ category }}</h3>
                    <table>
                        <thead>
                        <tr>
                            <th>ID</th>
                            <th>Descripción</th>
                        </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in items" :key="item.id">
                                <td>{{ item.id }}</td>
                                <td>{{ item.description || item.name }}</td>
                            </tr>
                        </tbody>
                    </table>
                    </div>
                </div>
                <p v-else>No data available</p>
            </card>
        </div>
        </q-modal>
    </page>
</template>

<script>
    import {
        QModal,
        QIcon,
        openURL
    } from 'quasar-framework'
    import Form from 'vform'
    import {mapGetters} from "vuex";
    import store from "../../../../../store";
    import { infoModalOpen, infoModalClose, fetchInfoContent } from '../../../../../pages/infoModalUtils';

    export default {
        middleware: 'auth',

        metaInfo() {
            return {title: this.$T('upload and import file')}
        },

        components: {
            QModal,
            QIcon
        },

        data() {

            let delimiterOptions = [
                {label: this.$T('semicolon (;)'), value: ';'},
                {label: this.$T('comma (,)'), value: ','}
            ]
            let form = {
                skip_first: true,
                delimiter: ';'
            }

            return {
                uploadErrors: [],
                uploader: {},
                dragModal: false,
                success: null,
                delimiterOptions,
                form: new Form(form),
                responseMessage: "",
                infoModal: false,
                infoContent: null,
            }
        },

        mounted() {
            this.uploader = this.$refs.file.$refs.uploader

            // Add listener for drag into the window
            window.addEventListener('dragover', this.onDragOver)
        },

        destroyed() {
            // Remove listener
            window.removeEventListener('dragover', this.onDragOver)
        },

        computed: {
            url() {
                return this.hasReverseFlowEnable ? `${config.appUrl}/api/v1/lending-point/import-with-reverse-flow` :
                    `${config.appUrl}/api/v1/lending-point/import`
            },

            uploading() {
                return this.uploader.uploading
            },

            serializedForm() {
                return Object.keys(this.form).map(name => ({name, value: this.form[name]}))
            },

            ...mapGetters({
                user: "auth/user",
            }),

            hasReverseFlowEnable() {
                if (!this.user) return false
                return this.user.market.configurations.some(config => {
                    return config.description === 'enable_reverse_flow' && parseInt(config.pivot.value) === 1
                })
            },

            getColumns() {

                let csvColumns = [
                    {label: this.$T('name'), value: 'description'},
                    {label: this.$T('isms code'), value: 'isms_code'}
                ]

                if (this.hasReverseFlowEnable) {
                    csvColumns.push({label: this.$T('lending point category'), value: 'lending_point_category'})
                }

                csvColumns.push(
                    {label: this.$T('lending point type'), value: 'lending_point_type'},
                    {label: this.$T('lending point trace'), value: 'lending_point_trace'},
                    {label: this.$T('segmentation'), value: 'segmentation'},
                    {label: this.$T('sanitization type'), value: 'sanitization_type_id'},
                    {label: this.$T('address'), value: 'address'},
                    {label: this.$T('address 2'), value: 'address_2'},
                    {label: this.$T('zip code'), value: 'zipcode'},
                    {label: this.$T('city'), value: 'city'},
                    {label: this.$T('nif'), value: 'nif'},
                    {label: this.$T('lat'), value: 'lat'},
                    {label: this.$T('lng'), value: 'lng'},
                    {label: this.$T('phone prefix'), value: 'phone_prefix'},
                    {label: this.$T('phone'), value: 'phone'},
                    {label: this.$T('sap number'), value: 'sap_number'},
                    {label: this.$T('store owner'), value: 'owner'},
                    {label: this.$T('touchpoint_id'), value: 'touchpoint_id'},
                    {label: this.$T('market'), value: 'market_id'},
                    {label: this.$T('allow lending creation'), value: 'enable_lending_creation'},
                    {label: this.$T('enable completed - sold'), value: 'enable_completed_sold'},
                    {label: this.$T('add new lending kit'), value: 'enable_add_lending_kit'})

                /*if (this.hasReverseFlowEnable) {
                    csvColumns.push({label: this.$T('2.4 PLUS min'), value: '2_4_plus_min'})
                    csvColumns.push({label: this.$T('2.4 PLUS max'), value: '2_4_plus_max'})
                    csvColumns.push({label: this.$T('DUO min'), value: 'duo_min'})
                    csvColumns.push({label: this.$T('DUO max'), value: 'duo_max'})
                }*/

                csvColumns.forEach((column, i) => this.form[`column${i}`] = column.value)

                return csvColumns
            }
        },

        methods: {
            onDrop(e) {
                this.uploader.__onDrop(e)
                this.dragModal = false
            },

            onDragOver(e) {
                e.stopPropagation()
                e.preventDefault()
                this.dragModal = true
            },

            onDragLeave(e) {
                this.dragModal = false
            },

            upload() {
                //Resets the success state
                this.success = null

                // Submit the file
                this.uploader.upload()
            },

            receiveData(xhr) {
                // Get the response string (it's a common xhr request. The data is a string in response)
                var responseData = null

                // Tries to parse the data
                try {
                    responseData = JSON.parse(xhr.response)
                } catch (e) {

                }


                if (responseData && responseData.success) {
                    //Success (Triggers a positive alert)
                    this.success = true
                    this.responseMessage = responseData.success.message
                }
                // If no form data or formData, alert the user
                else {
                    if (responseData.error) {
                        this.$q.notify({
                            message: responseData.error.message,
                            type: 'negative',
                            icon: 'warning',
                            position: 'top',
                            timeout: 0,
                            closeBtn: true
                        })
                    } else {
                        if (responseData.errors) {
                            this.form.errors.errors = responseData.errors

                            responseData.errors.forEach(item => {

                                Object.values(item).forEach(error => {
                                    this.$q.notify({
                                        message: error[0],
                                        type: 'negative',
                                        icon: 'warning',
                                        position: 'top',
                                        closeBtn: true
                                    })
                                })
                            })

                        }

                        //Error (=not success. Triggers a negative alert)
                        this.success = false
                    }
                }

                // Clear the files object in the uploader (the reset method triggers some xhr unwanted behavior)
                this.uploader.files = []
            },

            downloadSample(fileName) {
                openURL(`${window.config.appUrl}/samples/${fileName}?v=h134566cvky9002`)
            },

            infoModalOpen,
            infoModalClose,
            fetchInfoContent,
        },
    }
</script>



// WEBPACK FOOTER //
// resources/assets/js/markets/ES/pages/backoffice/lending-point/import.vue